import { Component, Inject } from '@angular/core';

import { FeatureGroupConfig, ImageMediaConfig, ProductConfig
       } from 'app/product-designer-config';
import { PriceCalculationService } from 'core/price/price-calculation.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AdditionalProductType } from './additional-product-type';

const IMAGE_INDEX_BY_TYPE: {[type in AdditionalProductType]: number} = {
  [AdditionalProductType.FRAME_2023]: 2,
  [AdditionalProductType.FRAME]: 1,
  [AdditionalProductType.FRAME_PLASTIC]: 1,
  [AdditionalProductType.GLUE]: 0,
  [AdditionalProductType.ROLL]: 1,
};

@Component({
    selector: 'additional-products-selection',
    templateUrl: './additional-products-selection.component.html',
    styleUrls: ['./additional-products-selection.component.scss'],
    standalone: false
})
export class AdditionalProductsSelectionComponent
{
  public productConfig: ProductConfig;
  public frame?: ProductConfig;
  public frameColorGroup?: FeatureGroupConfig;
  public glue?: ProductConfig;
  public roll?: ProductConfig;

  public readonly types: typeof AdditionalProductType = AdditionalProductType;
  public objToArray: any = Utils.objectValuesToArray;

  constructor(
      @Inject(ProductService) private productService,
      @Inject(EventTrackingService) private eventTrackingService,
      @Inject(PriceCalculationService) public priceCalculationService
      )
  {
    this.productConfig = this.productService.getCurrentProduct();

    const findByType = (...types: Array<AdditionalProductType>) =>
      this.productConfig.additionalProducts.find(
          p => types.includes(p.type as AdditionalProductType));

    this.frame = findByType(
        AdditionalProductType.FRAME_2023,
        AdditionalProductType.FRAME,
        AdditionalProductType.FRAME_PLASTIC);
    this.glue = findByType(AdditionalProductType.GLUE);
    this.roll = findByType(AdditionalProductType.ROLL);

    if (this.frame) {
      if (this.frame.type === AdditionalProductType.FRAME_2023) {
        this.frameColorGroup = Utils.byValue(this.frame.featureGroups)[0];
      }
    }
  }

  public selectFrameColor(featureId: string): void
  {
    this.frameColorGroup.selectedFeature = featureId;
  }

  public getImageURL(product: ProductConfig): string | undefined
  {
    const images: Array<ImageMediaConfig> = product === this.frame &&
        this.frameColorGroup
      ? this.frameColorGroup.features[this.frameColorGroup.selectedFeature]
          .images
      : product.images;

    return (images[IMAGE_INDEX_BY_TYPE[product.type]] ?? images[0])?.largeURL;
  }

  public setAdditionalProductIsSelected(
      productId: string, selected: boolean): void
  {
    const selectedProducts = this.productConfig.selectedAdditionalProducts;
    const index = selectedProducts.indexOf(productId);

    if (selected && index === -1) {
      selectedProducts.push(productId);
      this.trackSelection(productId, 1);
    }
    else if (!selected && index > -1) {
      selectedProducts.splice(index, 1);
      this.trackSelection(productId, 0);
    }

    this.priceCalculationService.calculateTotalPrice();
  }

  public additionalProductIsSelected(productId: string): boolean
  {
    return (
        this.productConfig.selectedAdditionalProducts.indexOf(productId) > -1);
  }

  private trackSelection(productId: string, value: number): void
  {
    this.eventTrackingService.track({
      category: this.productConfig.id,
      action: 'accessories_change',
      label: productId,
      value: value,
    });
  }
}
